import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

const ARCHETYPE_FAQS = [
  {
    q: "What are brand archetypes in branding?",
    a: "Brand archetypes are personality frameworks used in branding to humanize a brand. Based on Jungian psychology, they categorize brands into 12 personality types such as Hero, Creator, Caregiver, and Explorer. These archetypes help brands communicate consistently and emotionally connect with customers."
  },
  {
    q: "What are the 12 brand archetypes?",
    a: "The 12 brand archetypes commonly used in branding strategy are: Innocent, Sage, Explorer, Rebel, Magician, Hero, Lover, Jester, Caregiver, Creator, Ruler, and Everyman. Each archetype represents a distinct motivation, tone, and brand personality."
  },
  {
    q: "How do I find my brand archetype?",
    a: "You can identify your brand archetype by analyzing: Your brand's core mission and values, how your customers perceive your brand, the emotional benefit your brand delivers, and your tone of voice and communication style. A brand archetype quiz simplifies this process by translating these factors into a clear personality type."
  },
  {
    q: "What is a brand personality quiz?",
    a: "A brand personality quiz is a strategic tool that helps founders and marketers determine the emotional personality of their brand. By answering a set of questions about brand behavior, communication, and positioning, the quiz reveals the archetype that best represents the brand."
  },
  {
    q: "Why should a business define its brand archetype?",
    a: "Defining a brand archetype helps businesses build a recognizable brand identity, create consistent messaging, strengthen emotional customer connection, and guide design, tone, and marketing decisions."
  },
  {
    q: "Can a brand have multiple archetypes?",
    a: "Yes. Many brands operate with a primary archetype and a secondary supporting archetype. However, combining too many archetypes can dilute the brand personality and confuse the audience."
  },
  {
    q: "Are some brand archetypes incompatible?",
    a: "Yes. Some archetypes represent opposite values and can create conflicting brand signals. For example, Innocent and Rebel represent contrasting motivations, while Jester and Ruler communicate very different tones of authority."
  },
  {
    q: "How does a brand archetype affect brand identity?",
    a: "A brand archetype influences several aspects of branding, including visual identity and color palette, tone of voice and messaging, advertising style, customer experience, and brand storytelling."
  },
  {
    q: "Can startups use brand archetypes?",
    a: "Yes. Brand archetypes are particularly useful for startups because they help define a clear personality early, guiding brand naming, messaging, and design decisions."
  },
  {
    q: "What happens after I discover my brand archetype?",
    a: "Once your archetype is defined, it can be used to build brand voice guidelines, visual identity systems, marketing campaigns, and brand storytelling frameworks."
  },
  {
    q: "When is the best time in a brand's lifecycle to define its archetype?",
    a: "The best time to define a brand archetype is during the early stages of brand creation or positioning, ideally before developing the brand identity, messaging, and marketing strategy. Establishing the archetype early helps ensure that your tone of voice, visual identity, storytelling, and customer experience are aligned with a clear personality from the start. However, defining or revisiting your archetype is also valuable during brand repositioning, market expansion, or when the brand's communication has become inconsistent over time."
  },
];

const QUIZ_FAQS = [
  {
    q: "How long does the brand archetype quiz take?",
    a: "The quiz takes about 3\u20134 minutes to complete and consists of a short set of strategic questions about your brand's personality and behavior."
  },
  {
    q: "Who should take this brand archetype quiz?",
    a: "This quiz is designed for startup founders, business owners, marketing teams, brand strategists, and designers working on brand identity."
  },
  {
    q: "Is the quiz useful if I'm still creating my brand?",
    a: "Yes. If you are building a brand from scratch, the quiz helps clarify the personality and emotional positioning of the brand before you design the identity or messaging."
  },
  {
    q: "What will I learn from the quiz results?",
    a: "Your results will reveal your primary brand archetype, a possible secondary archetype, key personality traits of your brand, and strategic insights on tone, communication, and brand positioning."
  },
  {
    q: "Do I need branding knowledge to take this quiz?",
    a: "No. The quiz is designed to be simple and intuitive. You only need to answer questions based on how your brand behaves or should behave in the market."
  },
  {
    q: "Can this quiz help with brand positioning?",
    a: "Yes. Your archetype can act as a strategic foundation for defining brand voice, visual identity, marketing campaigns, and storytelling."
  },
  {
    q: "Is the quiz free to use?",
    a: "Yes. The quiz follows a freemium model. You can take the quiz for free and instantly see your primary brand archetype and basic insights. If you'd like a deeper analysis\u2014such as detailed personality traits, strategic recommendations, and brand application guidance\u2014you can unlock the extended results and report afterward."
  },
  {
    q: "What should I do after getting my archetype?",
    a: "After discovering your archetype, you can use the insights to develop brand voice guidelines, build a consistent visual identity, and align marketing communication with your brand personality."
  },
  {
    q: "Can I take the quiz if I already have a brand?",
    a: "Yes. The quiz is equally useful for existing brands as well as new ones. If your brand is already in the market, the quiz helps evaluate whether your current communication, positioning, and personality align with how customers perceive the brand. It can reveal your brand's natural archetype and highlight opportunities to strengthen consistency in messaging, design, and marketing."
  },
  {
    q: "When is the best time in a brand's lifecycle to take this quiz?",
    a: "This quiz can be valuable at multiple stages of a brand's lifecycle. It is especially useful when you are creating a new brand, repositioning an existing brand, or trying to bring consistency to your communication and identity. Taking the quiz early helps define a clear brand personality, while taking it later can help evaluate whether your current messaging and design still align with the brand's intended character."
  },
];

const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className="border-b border-gray-800 last:border-b-0">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between py-5 px-1 text-left group"
      aria-expanded={isOpen}
    >
      <span className={`text-sm sm:text-base font-medium pr-4 transition-colors ${isOpen ? 'text-white' : 'text-gray-300 group-hover:text-white'}`}>
        {question}
      </span>
      <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 text-gray-500 transition-transform duration-200 ${isOpen ? 'rotate-180 text-cyan-400' : ''}`} />
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-[500px] pb-5' : 'max-h-0'}`}
    >
      <p className="text-sm sm:text-base text-gray-400 leading-relaxed px-1">
        {answer}
      </p>
    </div>
  </div>
);

const FAQSection = () => {
  const [activeTab, setActiveTab] = useState('archetypes');
  const [openIndex, setOpenIndex] = useState(0); // First FAQ open by default

  const currentFaqs = activeTab === 'archetypes' ? ARCHETYPE_FAQS : QUIZ_FAQS;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setOpenIndex(0); // Reset to first FAQ open when switching tabs
  };

  // Generate FAQ Schema (JSON-LD) for all FAQs
  const allFaqs = [...ARCHETYPE_FAQS, ...QUIZ_FAQS];
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allFaqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <section className="py-12 sm:py-16 md:py-20" data-testid="faq-section">
      {/* FAQ Schema for SEO - JSON-LD with script-tag breakout escaping */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema).replace(/</g, '\\u003c') }}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-center text-white mb-3">
          Frequently Asked Questions
        </h2>
        <p className="text-center text-gray-400 mb-8 sm:mb-10 text-sm sm:text-base">
          Everything you need to know about brand archetypes and this quiz
        </p>

        {/* Tab Buttons */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-gray-900 rounded-lg p-1 border border-gray-800">
            <button
              onClick={() => handleTabChange('archetypes')}
              className={`px-4 sm:px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                activeTab === 'archetypes'
                  ? 'text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
              }`}
              style={activeTab === 'archetypes' ? { background: 'linear-gradient(to right, #00cffd, #7b00eb)' } : {}}
              data-testid="faq-tab-archetypes"
            >
              Archetype FAQs
            </button>
            <button
              onClick={() => handleTabChange('quiz')}
              className={`px-4 sm:px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
                activeTab === 'quiz'
                  ? 'text-white shadow-md'
                  : 'text-gray-400 hover:text-white'
              }`}
              style={activeTab === 'quiz' ? { background: 'linear-gradient(to right, #00cffd, #7b00eb)' } : {}}
              data-testid="faq-tab-quiz"
            >
              Quiz FAQs
            </button>
          </div>
        </div>

        {/* FAQ Accordion */}
        <div className="bg-gray-900/50 border border-gray-800 rounded-xl px-4 sm:px-6">
          {currentFaqs.map((faq, index) => (
            <FAQItem
              key={`${activeTab}-${index}`}
              question={faq.q}
              answer={faq.a}
              isOpen={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
